import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

import { useAuth, useDb } from '../firebase/FirebaseContext';

/**
 * The saved hidden-holiday list, READ ONLY (THE-764).
 *
 * Why this exists next to `hooks/useHolidayPrefs`. That hook is the Holidays
 * PAGE's hook: it reads *and* writes, so it imports `../callables`, and
 * `callables` → `firebaseFunctions.ts` runs
 * `getFunctions(getAppInstance())` AT MODULE LOAD. Importing it from a render
 * surface therefore drags the Functions SDK into the module graph of
 * everything that transitively imports that surface — `ComingUpStrip` is
 * imported by `FreshFindFridaysCard`, which is imported by
 * `BrowseMyFriendsSection`, and five suites that mock `firebaseConfig`
 * without `getAppInstance` died at COLLECTION time, before a single
 * assertion ran. That is a landmine every future importer would inherit, and
 * the fix is not to widen those mocks: the rendering surfaces do not save, so
 * they should not depend on the save path at all.
 *
 * So: the calendar and the Coming Up strip take this hook, the Holidays page
 * keeps `useHolidayPrefs`. The write-side bookkeeping there — optimistic
 * value, in-flight suppression, rollback on failure — is deliberately NOT
 * duplicated here; this is a subscription and nothing else.
 *
 * The raw stored value is returned as `unknown`, on purpose and unconverted.
 * It comes from a Firestore document, so its runtime shape is not guaranteed
 * by the `TheaWebUser` interface, and the one place allowed to decide what a
 * given shape MEANS is `filterHiddenHolidays` — shared with both surfaces so
 * one rule serves both. A `?? []` here would quietly turn a malformed
 * document into "she has hidden nothing".
 *
 * Never log the value: a user's holiday choices never leave our database, and
 * a browser console counts (THE-746).
 */
export interface HiddenHolidaysRead {
  /** The raw `theaWebUser.hiddenHolidays`, straight from the document. */
  hiddenHolidays: unknown;
  /**
   * Has the answer actually ARRIVED? `false` means no holiday may render:
   * before the first snapshot the list is indistinguishable from a genuine
   * "she has hidden nothing", and guessing all-on there renders exactly the
   * holidays she went to the Holidays page to hide.
   *
   * A signed-out or anonymous visitor is ready IMMEDIATELY with an absent
   * list — she has no saved document, so there is nothing to wait for and
   * every holiday is on. Without that, holidays would never render for the
   * anonymous traffic that makes up most of the marketing page.
   *
   * Stays `false` forever on a PERMANENT read failure (e.g. a denied rule) —
   * it never flips back to `true` on its own. A caller that needs to stop
   * waiting on this arm must also check `error` below; `ready` alone cannot
   * tell "still asking" apart from "will never get an answer".
   */
  ready: boolean;
  /**
   * Did the subscription's last attempt fail? Distinct from `ready` so a
   * caller can tell a permanent failure (THE-1134: a denied `theaWebUser/{uid}`
   * read) apart from a slow-but-still-in-flight first snapshot — both leave
   * `ready` at `false`, but only one of them is ever going to change. Reset to
   * `false` on the next successful snapshot or auth-scope change, so a
   * transient blip does not stick forever.
   */
  error: boolean;
}

export function useHiddenHolidays(): HiddenHolidaysRead {
  const auth = useAuth();
  const db = useDb();
  const [hiddenHolidays, setHidden] = useState<unknown>(undefined);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  // Identifies the current auth scope, so a snapshot that arrives after a
  // sign-out (or a switch to another account) cannot apply its value to the
  // new user. Compared by reference, like `useHolidayPrefs` does.
  const scope = useRef<object>({});

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const stopAuth = onAuthStateChanged(auth, (user) => {
      unsubscribe?.();
      const current = {};
      scope.current = current;
      const uid = user && !user.isAnonymous ? user.uid : '';
      setHidden(undefined);
      setError(false);
      // Signed out / anonymous: ready at once, with no document to read.
      setReady(!uid);
      if (!uid) return;
      unsubscribe = onSnapshot(
        doc(db, 'theaWebUser', uid),
        (snapshot) => {
          if (scope.current !== current) return;
          setHidden((snapshot.data() as { hiddenHolidays?: unknown } | undefined)?.hiddenHolidays);
          setReady(true);
          setError(false);
        },
        () => {
          // Read failed. Stay NOT ready: we cannot tell "hidden nothing" from
          // "could not ask", and only one of those is safe to render. `error`
          // is what lets a caller stop WAITING on this arm without guessing
          // which of those two it is.
          if (scope.current !== current) return;
          setReady(false);
          setError(true);
        },
      );
    });
    return () => {
      stopAuth();
      unsubscribe?.();
      scope.current = {};
    };
  }, [auth, db]);

  return { hiddenHolidays, ready, error };
}

export default useHiddenHolidays;
